import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { blogPostsQueryOptions } from "@/api-queries/blog";
import { BlogCard } from "@/components/blog/BlogCard";
import { Pagination } from "@/components/ui/pagination";
import { cn } from "@/lib/utils";
import { BLOG_CATEGORIES, BLOG_CATEGORY_LABELS, isBlogCategory } from "@/lib/blog-categories";
import { defineSection } from "@/ssr/define-section";
import { ssrPage } from "@/ssr/define-page";
import { definePageMeta } from "@/ssr/define-meta";
import { buildPageStructuredData } from "@/ssr/structured-data";

const blogMeta = definePageMeta({
  titleKey: "blog.meta.title",
  descriptionKey: "blog.meta.description",
  ogImage: "/og-blog.jpg",
  structuredData: (ctx) => buildPageStructuredData(ctx, {
    titleKey: "blog.meta.title",
    descriptionKey: "blog.meta.description",
    ogImage: "/og-blog.jpg",
    pageType: "Blog",
    breadcrumbs: [
      { name: "Psihotron", path: "/" },
      { name: ctx.t("nav.blog"), path: "/blog" },
    ],
  }),
});

const POSTS_PER_PAGE = 9;

interface BlogSearch {
  author?: string;
  therapist?: string;
  cat?: string;
  q?: string;
  page?: number;
}

/** Single source of truth for the list query options (matches between SSR + client). */
function listOptions(search: BlogSearch) {
  return blogPostsQueryOptions({
    status: "published",
    limit: POSTS_PER_PAGE,
    page: search.page ?? 1,
    ...(search.author ? { authorId: search.author } : {}),
    ...(search.therapist ? { therapistId: search.therapist } : {}),
    ...(search.cat ? { category: search.cat } : {}),
    ...(search.q ? { q: search.q } : {}),
  });
}

function BlogIndexPageView() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: "/blog" });
  const search = Route.useSearch();
  const { author, therapist, cat, q, page = 1 } = search;

  // Search input: local state synced to the `q` URL param with a small debounce.
  const [queryInput, setQueryInput] = useState(q ?? "");
  useEffect(() => setQueryInput(q ?? ""), [q]);
  useEffect(() => {
    const trimmed = queryInput.trim();
    if (trimmed === (q ?? "")) return;
    const id = setTimeout(() => {
      navigate({ search: (prev: BlogSearch) => ({ ...prev, q: trimmed || undefined, page: undefined }) });
    }, 300);
    return () => clearTimeout(id);
  }, [queryInput]);

  const { data, isLoading, error } = useQuery(listOptions(search));

  const posts: any[] = data?.data ?? (Array.isArray(data) ? data : []);
  const total: number = data?.total ?? posts.length;
  const totalPages = Math.ceil(total / POSTS_PER_PAGE);

  const setCategory = (next?: string) =>
    navigate({ search: (prev: BlogSearch) => ({ ...prev, cat: next, page: undefined }) });
  const setPage = (next: number) =>
    navigate({ search: (prev: BlogSearch) => ({ ...prev, page: next === 1 ? undefined : next }) });

  const authorName: string | undefined = author || therapist
    ? (posts[0]?.authorName as string | undefined)
    : undefined;
  const heroTitle = author || therapist ? authorName ?? t("blog.authorTitle") : t("blog.title");
  const heroSubtitle = author || therapist ? t("blog.authorSubtitle") : t("blog.subtitle");

  return (
    <div className="min-h-screen">
      {/* Hero section */}
      <section className="py-16 sm:py-20" style={{ background: "oklch(98% 0.005 84)" }}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <span
            className="text-xs font-semibold uppercase tracking-[0.1em]"
            style={{ color: "oklch(65% 0.15 84)" }}
          >
            {t("blog.badge")}
          </span>
          <h1
            className="mt-2 text-3xl font-bold text-foreground sm:text-4xl lg:text-5xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            {heroTitle}
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">{heroSubtitle}</p>

          {/* Search */}
          <div className="mx-auto mt-8 flex max-w-md items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 shadow-sm">
            <MagnifyingGlass className="size-4 shrink-0 text-muted-foreground" />
            <input
              type="search"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder={t("blog.searchPlaceholder")}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>
      </section>

      {/* Category chips */}
      {!author && !therapist && (
        <div className="border-b border-border bg-white/60">
          <div className="mx-auto flex max-w-7xl flex-wrap gap-2 px-4 py-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setCategory(undefined)}
              className={cn(
                "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                !cat
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-white text-foreground hover:bg-muted"
              )}
            >
              {t("blog.allTopics")}
            </button>
            {BLOG_CATEGORIES.map((tag) => (
              <button
                key={tag}
                onClick={() => setCategory(tag)}
                className={cn(
                  "rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors",
                  cat === tag
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-white text-foreground hover:bg-muted"
                )}
              >
                {BLOG_CATEGORY_LABELS[tag]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Blog grid */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {isLoading && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse rounded-xl border border-border bg-card">
                  <div className="aspect-[16/9] bg-muted rounded-t-xl" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 w-24 rounded bg-muted" />
                    <div className="h-5 w-full rounded bg-muted" />
                    <div className="h-3 w-full rounded bg-muted" />
                    <div className="h-3 w-2/3 rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t("blog.loadError")}</p>
            </div>
          )}

          {!isLoading && !error && posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {q || cat ? t("blog.noResults") : t("blog.noPosts")}
              </p>
            </div>
          )}

          {!isLoading && posts.length > 0 && (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {posts.map((post: any) => (
                  <BlogCard key={post.id} post={post} />
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

const BlogIndexSection = defineSection({
  Component: BlogIndexPageView,
  queries: [listOptions({})],
});

function BlogIndexPage() {
  return <BlogIndexSection.Component />;
}

export const Route = createFileRoute("/blog/")({
  ...ssrPage({
    component: BlogIndexPage,
    sections: [BlogIndexSection],
    meta: blogMeta,
  }),
  validateSearch: (search: Record<string, unknown>): BlogSearch => ({
    author: typeof search.author === "string" ? search.author : undefined,
    therapist: typeof search.therapist === "string" ? search.therapist : undefined,
    cat: isBlogCategory(search.cat) ? search.cat : undefined,
    q: typeof search.q === "string" && search.q.trim() ? search.q.trim() : undefined,
    page: typeof search.page === "number" && search.page > 1 ? search.page : undefined,
  }),
});
